import React from 'react';
import { XMarkIcon, MapPinIcon, BoltIcon, ChartBarIcon } from '@heroicons/react/24/outline';
import { Customer } from '../../types/customer';

interface PodListModalProps {
  isOpen: boolean;
  onClose: () => void;
  customer: Customer | null;
}

const PodListModal: React.FC<PodListModalProps> = ({ isOpen, onClose, customer }) => {
  if (!isOpen || !customer) return null;

  const allPods = [];
  
  // Add main POD
  if (customer.pod) {
    allPods.push({
      ...customer.pod,
      type: 'Fő POD',
      index: 0
    });
  }
  
  // Add additional PODs
  if (customer.additionalPods && Array.isArray(customer.additionalPods)) {
    customer.additionalPods.forEach((pod, index) => {
      allPods.push({
        ...pod,
        type: `További POD ${index + 1}`,
        index: index + 1
      });
    });
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div 
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
        />

        {/* Modal panel */}
        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
          {/* Header */}
          <div className="bg-gradient-to-r from-primary-600 to-primary-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold text-white">
                  POD Lista
                </h3>
                <p className="text-sm text-primary-100 mt-1">
                  {customer.companyName || `${customer.firstName} ${customer.lastName}`}
                </p>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:text-primary-100 transition-colors"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-4 max-h-[70vh] overflow-y-auto">
            {allPods.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                Nincs POD hozzárendelve ehhez az ügyfélhez
              </div>
            ) : (
              <div className="space-y-4">
                {allPods.map((pod, idx) => (
                  <div 
                    key={idx}
                    className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow bg-white"
                  >
                    {/* POD Header */}
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className="p-2 rounded-lg bg-primary-50 border border-primary-200">
                          <BoltIcon className="h-5 w-5 text-primary-600" />
                        </div>
                        <div>
                          <div className="text-sm font-semibold text-gray-900">
                            {pod.type}
                          </div>
                          <div className="text-xs text-gray-500 font-mono">
                            {pod.id}
                          </div>
                        </div>
                      </div>
                      {pod.meteringType && (
                        <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800 border border-blue-200">
                          {pod.meteringType}
                        </span>
                      )}
                    </div>

                    {/* POD Details Grid */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
                      {/* Address */}
                      {pod.address && (
                        <div className="flex items-start gap-2">
                          <MapPinIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Cím</div>
                            <div className="text-sm text-gray-900">{pod.address}</div>
                          </div>
                        </div>
                      )}

                      {/* Partner ID */}
                      {pod.partnerId && (
                        <div className="flex items-start gap-2">
                          <div className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0">🤝</div>
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Partneri azonosító</div>
                            <div className="text-sm text-gray-900">{pod.partnerId}</div>
                          </div>
                        </div>
                      )}

                      {/* Annual Consumption */}
                      {pod.annualConsumption && (
                        <div className="flex items-start gap-2">
                          <ChartBarIcon className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Éves fogyasztás</div>
                            <div className="text-sm text-gray-900">
                              {parseFloat(pod.annualConsumption).toLocaleString('hu-HU')} kWh
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Measurement Type */}
                      {pod.measurementType && (
                        <div className="flex items-start gap-2">
                          <div className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0">📊</div>
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Mérés típusa</div>
                            <div className="text-sm text-gray-900">{pod.measurementType}</div>
                          </div>
                        </div>
                      )}

                      {/* Multiplier */}
                      {pod.multiplier && (
                        <div className="flex items-start gap-2">
                          <div className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0">×</div>
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Szorzó</div>
                            <div className="text-sm text-gray-900">{pod.multiplier}</div>
                          </div>
                        </div>
                      )}

                      {/* Billable Status */}
                      {pod.billable !== undefined && (
                        <div className="flex items-start gap-2">
                          <div className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0">💰</div>
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Számlázható</div>
                            <div className="text-sm text-gray-900">
                              {pod.billable ? (
                                <span className="text-green-600 font-medium">✓ Igen</span>
                              ) : (
                                <span className="text-red-600 font-medium">✗ Nem</span>
                              )}
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Meter Serial Number */}
                      {pod.meterSerialNumber && (
                        <div className="flex items-start gap-2">
                          <div className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0">🔢</div>
                          <div className="min-w-0">
                            <div className="text-xs text-gray-500">Mérő sorozatszám</div>
                            <div className="text-sm text-gray-900 font-mono">{pod.meterSerialNumber}</div>
                          </div>
                        </div>
                      )}
                    </div>

                    {/* Small Power Plant Info */}
                    {pod.smallPowerPlant && pod.smallPowerPlant.type !== 'none' && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <div className="flex items-center gap-2 mb-2">
                          <span className="text-lg">☀️</span>
                          <span className="text-sm font-semibold text-gray-900">
                            {pod.smallPowerPlant.type === 'hmke' ? 'HMKE' : 'Kiserőmű'}
                          </span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-xs">
                          {pod.smallPowerPlant.inverterCapacityKva && (
                            <div className="text-gray-600">
                              Inverter kapacitás: <span className="font-medium text-gray-900">{pod.smallPowerPlant.inverterCapacityKva} kVA</span>
                            </div>
                          )}
                          {pod.smallPowerPlant.feedInCapacityKva && (
                            <div className="text-gray-600">
                              Betáplálási teljesítmény: <span className="font-medium text-gray-900">{pod.smallPowerPlant.feedInCapacityKva} kVA</span>
                            </div>
                          )}
                          {pod.smallPowerPlant.settlementType && (
                            <div className="text-gray-600">
                              Elszámolás: <span className="font-medium text-gray-900">
                                {pod.smallPowerPlant.settlementType === 'annual_balance' ? 'Éves szaldó' :
                                 pod.smallPowerPlant.settlementType === 'gross' ? 'Bruttó' : 'Függőben'}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between items-center">
            <div className="text-sm text-gray-600">
              Összesen: <span className="font-semibold text-gray-900">{allPods.length} POD</span>
            </div>
            <button
              onClick={onClose}
              className="px-4 py-2 bg-white border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500"
            >
              Bezárás
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PodListModal;
